/**
 * タスクの型と表示用のユーティリティ。
 *
 * API が入るまでは画面側にサンプルを置いて見た目を固める。API 接続時にこの型を
 * そのまま契約として使い、`SAMPLE_TASKS` を差し替える。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business タスクの型定義と、期日による絞り込み・並べ替えを提供する。
 * @graph-connects none
 */

/**
 * タスクの進行状況。
 */
export type TaskStatus = '未着手' | '対応中' | '対応済';

/**
 * タスクの所属チーム。
 *
 * 現行環境の team_id に相当する区分。タスクが混ざらないよう、画面はどちらか一方を
 * 選んで表示する。
 *
 * - `sft`: ストラテジックファイナンスチーム
 * - `cpt`: コーポレートサポートチーム
 */
export type TeamId = 'cpt' | 'sft';

/** 選べるチーム。記憶した値の検証にも使う */
/** @graph-connects none */
export const TEAM_IDS: readonly TeamId[] = ['sft', 'cpt'];

/**
 * タスク 1 件。
 */
export interface Task {
  /** 一意な ID */
  id: string;
  /** 所属チーム */
  team: TeamId;
  /** 種別（会議 / 月次経理 / 決算 / その他タスク など） */
  kind: string;
  /** 内容 */
  title: string;
  /**
   * 想定時間（時間）。決めていないものは null。
   *
   * 月次経理（FMT）の型から起こしたタスクが引き継ぐ。手で作ったタスクやカレンダー
   * 取り込みのタスクには無いため任意にしている。大分類・事業・中分類は列を持たせず、
   * 内容（title）に畳み込んでいる（FMT 側が分類の正）。
   */
  estimatedHours?: number | null;
  /**
   * 何月分の月次か（`YYYY-MM`）。FMT でタスクを作るときに選んだ対象月。
   *
   * 対応日だけでは、どの月の締めのための作業かが分からない（月をまたいで
   * 対応することもある）。月次経理として起こしたタスクだけが持つ
   */
  targetMonth?: string;
  /** 覚え書き。作業のときに見る手順や、引き継ぎのための一言を置く */
  note?: string;
  /**
   * 作業の手順書（HTML）。月次経理の型（FMT）から起こしたときに写してくる。
   *
   * 覚え書きとは別にしている。覚え書きはそのタスク限りの一言で、手順書は毎月同じ
   * 内容を見るもの。直すのは FMT 側で、タスクからは見るだけにする
   */
  manual?: string;
  /** どこから作られたタスクか */
  source?: 'manual' | 'calendar' | 'fmt';
  /** 作成元の ID（FMT の項目 ID / カレンダーの予定 ID） */
  sourceRef?: string;
  /** 担当者 */
  assignee: string;
  /** 対応日（`YYYY-MM-DD`）。画面では「対応日」と呼ぶ */
  dueDate: string;
  /**
   * 開始時刻（`HH:MM`）。
   *
   * カレンダーから取り込んだ予定には入るが、手で作ったタスクには無い。無いものは
   * 時間軸カレンダーの「終日」の帯に置く。
   */
  startTime?: string;
  /** 終了時刻（`HH:MM`）。無い場合は既定の長さで描く */
  endTime?: string;
  /** 進行状況 */
  status: TaskStatus;
}

/** 選べる状況。プルダウンやタブの並び順にも使う */
/** @graph-connects none */
export const STATUSES: readonly TaskStatus[] = ['未着手', '対応中', '対応済'];

/**
 * 状況の並び順。未着手を上に出す。
 * @graph-connects none
 */
const STATUS_ORDER: Record<TaskStatus, number> = {
  未着手: 0,
  対応中: 1,
  対応済: 2,
};

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 指定チームのタスクだけに絞る。CPT と SFT を混ぜて表示しないため。
 * @graph-connects none
 */
export function filterByTeam(tasks: readonly Task[], team: TeamId): Task[] {
  return tasks.filter((task) => task.team === team);
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 1 日で終わらなかったタスクの続きを別の日に置くため、同じ中身で未着手の写しを作る。カレンダーから取り込んだ予定の写しは手で作ったものとして扱う（取り込み直しで上書きされないように）
 * @graph-connects none
 */
export function duplicateTask(task: Task): Omit<Task, 'id'> {
  const { id: _id, ...rest } = task;
  if (task.source === 'calendar') {
    const { sourceRef: _ref, ...manual } = rest;
    return { ...manual, source: 'manual', status: '未着手' };
  }
  return { ...rest, status: '未着手' };
}

/** 「全員」を表す値。担当者名と衝突しないよう空文字を使う */
/** @graph-connects none */
export const ALL_ASSIGNEES = '';

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 指定担当者のタスクだけに絞る。`ALL_ASSIGNEES` なら全員分を返す。
 * @graph-connects none
 */
export function filterByAssignee(tasks: readonly Task[], assignee: string): Task[] {
  if (assignee === ALL_ASSIGNEES) {
    return [...tasks];
  }
  return tasks.filter((task) => task.assignee === assignee);
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business タスクに出てくる担当者を重複なく名前順で返す。プルダウンの選択肢に使う。
 * @graph-connects none
 */
export function listAssignees(tasks: readonly Task[]): string[] {
  return [...new Set(tasks.map((task) => task.assignee))].sort((a, b) => a.localeCompare(b, 'ja'));
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 担当者の選択肢を作る。メンバー設定の人を設定の並び順で先に置き、タスクにしか出てこない人を後ろに足す。タスクがまだ無い人も選べるようにするため
 * @graph-connects listAssignees [calls] タスクに出てくる担当者を取り出す
 */
export function listAssigneeOptions(
  tasks: readonly Task[],
  members: readonly { name: string; order: number; team?: TeamId }[],
  // 渡したときは、そのチームのメンバーだけを候補にする（他チームの人を選べても意味がない）
  team?: TeamId,
): string[] {
  const memberNames = members
    .filter((member) => team === undefined || member.team === team)
    .sort((a, b) => a.order - b.order)
    .map((member) => member.name);
  const registered = new Set(memberNames);
  return [...new Set(memberNames), ...listAssignees(tasks).filter((name) => !registered.has(name))];
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 期日が指定範囲（両端を含む）にあるタスクだけに絞る。`YYYY-MM-DD` は文字列比較で日付順になる。
 * @graph-connects none
 */
export function filterByDateRange(
  tasks: readonly Task[],
  startDate: string,
  endDate: string,
): Task[] {
  return tasks.filter((task) => task.dueDate >= startDate && task.dueDate <= endDate);
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 期日の昇順、同日なら未着手を先に並べる。
 * @graph-connects none
 */
export function sortForTable(tasks: readonly Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    if (a.dueDate !== b.dueDate) {
      return a.dueDate < b.dueDate ? -1 : 1;
    }
    return STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
  });
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 期日ごとにタスクをまとめる。カレンダーの日セルに入れるため。
 * @graph-connects none
 */
export function groupByDueDate(tasks: readonly Task[]): Map<string, Task[]> {
  const grouped = new Map<string, Task[]>();
  for (const task of tasks) {
    const same = grouped.get(task.dueDate);
    if (same === undefined) {
      grouped.set(task.dueDate, [task]);
    } else {
      same.push(task);
    }
  }
  return grouped;
}
